/*eslint-disable @typescript-eslint/explicit-module-boundary-types*/

import axios from "axios";
import {IANAZone} from 'luxon';
import jwt_decode from "jwt-decode";
import {FinixErrorCodeMapper} from "@/app/utils/common/functions/payfac-error-code-mapper/finix/error-code-mapper";
import {AdyenErrorCodeMapper} from "@/app/utils/common/functions/payfac-error-code-mapper/adyen/error-code-mapper";
import cbStorage from "@/app/utils/common/core/cb-storage";
import {CbStorage} from "@/app/utils/common/constants";
import { TimeZoneList } from "@/app/utils/common/helpers/TimeZoneList";

interface JwtToken {
    application_type: string;
    exp: number;
    iat: number;
    iss: string;
    sub: string;
}

export const appDefaultState = {
    adminMode: false,
    merchantId: null,
    applicationId: null,
    applicationName: null,
    merchantName: null,
    merchantCreatedAt: null,
    merchants: [],
    recentlySelectedMerchants: [],
    externalId: null,
    draftMerchantId: null,
    autoSavedDraftMerchantId: null,
    callbackUrl: null,
    clientIpAddress: null,
    timezone: null,
    merchantAlreadyStored: false,
    jwtToken: null,
    merchantPayfac: null,
    sessionPayfac: null,
    cardPricing: null,
    ACHPricing: null,
    BACSPricing: null,
    SEPAPricing: null,
    paymentMethodConfig: null,
    prefillData: null,
    rollingReserveConfig: null,
    upfrontReserveConfig: null,
    cbSiteName: null,
    isSandbox: false,
    merchantCurrency: null
};

const getters = {
    getClientIpAddress: s => s.clientIpAddress,
    isAdminMode: s => s.adminMode,
    getMerchantId: s => s.merchantId,
    getMerchants: s => s.merchants,
    getApplicationId: s => s.applicationId,
    getRecentlySelectedMerchants: s => s.recentlySelectedMerchants,
    getApplicationName: s => s.applicationName,
    getMerchantName: s => s.merchantName,
    getMerchantCreatedAt: s => s.merchantCreatedAt,
    getExternalId: s => s.externalId,
    getDraftMerchantId: s => s.draftMerchantId,
    getAutoSavedDraftMerchantId: s => s.autoSavedDraftMerchantId,
    getCallbackUrl: s => s.callbackUrl,
    getTimezone: s => s.timezone,
    isMerchantAlreadyStored: s => s.merchantAlreadyStored,
    getJwtToken: s => s.jwtToken,
    getCardPricing: s => s.cardPricing,
    getACHPricing: s => s.ACHPricing,
    getBACSPricing: s => s.BACSPricing,
    getSEPAPricing: s => s.SEPAPricing,
    getPaymentMethodConfig: s => s.paymentMethodConfig,
    getPrefillData: s => s.prefillData,
    getRollingReserveConfig: s => s.rollingReserveConfig,
    getUpfrontReserveConfig: s => s.upfrontReserveConfig,
    //This is merchant payfac
    getActivePayfac: s => s.merchantPayfac,
    //This is session payfac
    getSessionPayfac: s => s.sessionPayfac,
    getPayfacErrorCodeMapper: s => {
        switch(s.sessionPayfac){
            case "FINIX":
                return new FinixErrorCodeMapper();
            case "ADYEN":
                return new AdyenErrorCodeMapper();
        }
    },
    hideOnboardingPage: () => {
        //TODO: to be handled for prod-eu after testing
        if (process.env.VUE_APP_BUILD_ENV !== "prod") {
            return false;
        }

        if (cbStorage.getItem(CbStorage.JwtToken)){
            // @ts-ignore
            const decoded = jwt_decode<JwtToken>(cbStorage.getItem(CbStorage.JwtToken));
            return !decoded.application_type || decoded.application_type === "sandbox";
        }
    },
    getCbSiteName: s => s.cbSiteName,
    getIsSandbox: s => s.isSandbox,
    getMerchantCurrency: s => s.merchantCurrency
}

const IPIFY = 'https://api.ipify.org';

const actions = {
    async storeTimezone({commit, state}, timezone : string) {
        if (state.timezone === null || state.timezone === undefined) {
            const isValidTz = TimeZoneList.find(x=> x.value === timezone);
            if (isValidTz) {
                const canonicalTzVal = isValidTz.canonicalValue ?? isValidTz.value;
                commit('updateAppState', {key: 'timezone', value: new IANAZone(canonicalTzVal)});
            } else {
                commit('updateAppState', {key: 'timezone', value: new IANAZone('UTC')});
            }
        }
    },
    updateAppState({commit, dispatch}, {key, value}) {
        if (key === 'adminMode') {
            dispatch('updateAdminMode', value);
            return
        }

        if (key === 'applicationName') {
            dispatch('updateApplicationName', value);
            return
        }
        commit('updateAppState', {key, value})
    },
    updateAdminMode({commit}, adminMode) {
        cbStorage.setItem(CbStorage.AdminMode, adminMode);   
        commit('updateAppState', {key: 'adminMode', value: adminMode})
    },
    updateApplicationName({commit}, applicationName) {
        cbStorage.setItem(CbStorage.ApplicationName, applicationName)
        commit('updateAppState', {key: 'applicationName', value: applicationName})  
    },
    async storeClientIpAddress({commit}) {
        const ipAddress = (await axios.get(IPIFY)).data
        commit('updateAppState', {key: 'clientIpAddress', value: ipAddress})
    },
};

const mutations = {
    updateAppState(state, {key, value}) {
        state[key] = value
    },
};

export const app = {
    namespaced: true,
    state: appDefaultState,
    getters,
    actions,
    mutations,
};
